import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addIdKeyIfMissing, Button, Select, translateMessage } from "@delibrandev/utils";
import { BsPrinter, BsTrash } from "react-icons/bs";
import { RiDiscountPercentLine } from "react-icons/ri";
import { MdClose, MdEditNote, MdMoreHoriz } from "react-icons/md";

import { addCartDiscount, addCartNote, removeCartDiscount } from "../../../api/cart";
import { addOrderDiscount, addOrderNote, getOrderDetails, printTicket, removeOrderDiscount } from "../../../api/orders";
import { setAlert, updateCart, updateOrder } from "../../../reducers/userConfigSlice";
import { flushAllProductChanges } from "../../../services/productSyncQueue";
import { store } from "../../../store";

import "./payments.css";
import "./payments-options.css";

const notesToText = (notes) => {
  if (Array.isArray(notes)) {
    return notes
      .map((note) => {
        if (typeof note === "string") return note;
        return note?.note ?? note?.text ?? note?.value ?? "";
      })
      .filter(Boolean)
      .join("\n");
  }

  return typeof notes === "string" ? notes : "";
};

const getDiscountId = (discount) =>
  discount?.discountId ?? discount?.id ?? discount?.Discount?.discountId ?? discount?.Discount?.id ?? null;
const getDiscountName = (discount) =>
  discount?.name ?? discount?.title ?? discount?.code ?? discount?.Discount?.name ?? discount?.Discount?.title ?? "Descuento";

const ensureProductChangesSaved = async (dispatch) => {
  const synchronized = await flushAllProductChanges();
  if (!synchronized) {
    dispatch(
      setAlert({ called: true, title: "Sincronización", message: "La operación se ha cancelado porque hay cambios sin guardar." }),
    );
  }
  return synchronized;
};

const NotesForm = ({ notes, onSave, onClose }) => {
  const dispatch = useDispatch();
  const pending = useRef(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    if (pending.current) return;
    if (!note.trim()) { setError("Escribe una nota antes de guardar."); return; }
    pending.current = true;
    setBusy(true);
    setError("");
    try { await onSave(note.trim()); }
    catch (failure) {
      const message = failure?.message || "No se confirmó el guardado. Conserva la nota y reintenta.";
      setError(message);
      dispatch(setAlert({ called: true, type: "error", title: "Notas", message }));
    } finally { pending.current = false; setBusy(false); }
  };
  return <form className="form noteSaveForm" onSubmit={submit} aria-busy={busy}>
    {notesToText(notes) && <div><b>Notas guardadas</b><p style={{ whiteSpace: "pre-wrap" }}>{notesToText(notes)}</p></div>}
    <label htmlFor="new-order-note">Nueva nota</label>
    <textarea id="new-order-note" placeholder="Escribe una nota..." value={note} disabled={busy} onChange={event => setNote(event.target.value)} />
    {error && <p role="alert">{error}</p>}
    <div className="flex-end">
      <button type="submit" className="customButtonColor1" disabled={busy}>{busy ? "Guardando…" : "Guardar nota"}</button>
      <button type="button" className="customButtonColor2" disabled={busy} onClick={onClose}>Cerrar</button>
    </div>
  </form>;
};

const OrderDiscountComponent = ({ order, onClose }) => {
  const dispatch = useDispatch();
  const discounts = useSelector((state) => state.aplicationConfig.corporationData.discounts);

  const availableDiscounts = useMemo(
    () => addIdKeyIfMissing((Array.isArray(discounts) ? discounts : []).map((item) => ({ ...item }))),
    [discounts],
  );

  const appliedDiscounts = Array.isArray(order?.Discounts) ? order.Discounts : [];

  const refreshOrder = async () => {
    const response = await getOrderDetails(order?.orderId);
    if (response && !response.error) {
      dispatch(updateOrder(response?.data?.order ?? response?.data));
    }
  };

  const applyDiscount = async () => {
    try {
      if (!(await ensureProductChangesSaved(dispatch))) return;
      const select = document.getElementById("discount");
      const discountId = select?.value ?? null;

      if (!discountId || !order?.orderId) {
        dispatch(setAlert(translateMessage("Selecciona un descuento")));
        return;
      }

      const response = await addOrderDiscount({ orderIds: [order.orderId] }, discountId);

      if (response && !response.error) {
        await refreshOrder();

        dispatch(setAlert(translateMessage(response?.message || "Descuento aplicado")));
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      }
    } catch (error) {
      console.error("[OrderOptions] Error en addOrderDiscount:", error);
      dispatch(setAlert(translateMessage("Error al aplicar el descuento")));
    }
  };

  const removeDiscount = async (discount) => {
    const discountId = getDiscountId(discount);
    if (!discountId) return;

    try {
      if (!(await ensureProductChangesSaved(dispatch))) return;
      const response = await removeOrderDiscount(order?.orderId, discountId);

      if (response && !response.error) {
        await refreshOrder();

        dispatch(setAlert(translateMessage(response?.message || "Descuento eliminado")));
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      }
    } catch (error) {
      console.error("[OrderOptions] Error eliminando descuento:", error);
      dispatch(setAlert(translateMessage("Error al eliminar el descuento")));
    }
  };

  return (
    <div className="form">
      {appliedDiscounts.length > 0 && (
        <div className="applied-discounts">
          <b>Descuentos aplicados</b>
          {appliedDiscounts.map((discount, index) => {
            const discountId = getDiscountId(discount);
            return (
              <div className="applied-discount-item" key={discountId ?? index}>
                <span>{getDiscountName(discount)}</span>
                <Button text="Eliminar" icon={null} customClass="customButtonDanger" action={() => removeDiscount(discount)} />
              </div>
            );
          })}
        </div>
      )}

      <div>
        <Select id="discount" label="" values={availableDiscounts} value={availableDiscounts?.[0]} callback={() => null} />
      </div>

      <div className="flex-end">
        <Button text="Aplicar descuento" icon={null} customClass="customButtonColor1" action={applyDiscount} />
        <Button text="Cerrar" icon={null} customClass="customButtonColor2" action={onClose} />
      </div>
    </div>
  );
};

const OrderNotesComponent = ({ order, onClose }) => {
  const dispatch = useDispatch();
  const saveNote = async (note) => {
    if (!(await ensureProductChangesSaved(dispatch))) throw Error("Hay cambios de productos sin guardar. Reintenta cuando estén sincronizados.");
    if (!order?.orderId) throw Error("No hay pedido activo.");
    const response = await addOrderNote({ note }, order.orderId);
    const updated = response?.data?.order ?? response?.data;
    if (response?.error !== false || updated?.orderId !== order.orderId)
      throw Error(response?.message || "No se confirmó el guardado de la nota.");
    dispatch(updateOrder(updated));
    onClose();
    dispatch(setAlert({ called: true, title: "Notas", message: "Nota guardada en el pedido" }));
  };
  return <NotesForm notes={order?.notes} onSave={saveNote} onClose={onClose} />;
};

const CartNotesComponent = ({ cart, onClose }) => {
  const dispatch = useDispatch();
  const saveNote = async (note) => {
    if (!(await ensureProductChangesSaved(dispatch))) throw Error("Hay cambios de productos sin guardar. Reintenta cuando estén sincronizados.");
    const current = store.getState().aplicationConfig.userConfig.cart;
    if (!current?.shoppingCartId || current.shoppingCartId !== cart?.shoppingCartId) throw Error("El carrito ha cambiado. Cierra y vuelve a abrir las notas.");
    const response = await addCartNote({ note }, current.shoppingCartId);
    const updated = response?.data?.shoppingCart ?? response?.data?.cart ?? response?.data;
    if (response?.error !== false || updated?.shoppingCartId !== current.shoppingCartId)
      throw Error(response?.message || "No se confirmó el guardado de la nota.");
    if (store.getState().aplicationConfig.userConfig.cart?.shoppingCartId === current.shoppingCartId)
      dispatch(updateCart({ ...current, ...updated }));
    onClose();
    dispatch(setAlert({ called: true, title: "Notas", message: "Nota guardada en el carrito" }));
  };
  return <NotesForm notes={cart?.notes} onSave={saveNote} onClose={onClose} />;
};

export const CartDiscountComponent = ({ cart, onClose }) => {
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const dispatch = useDispatch();
  const discounts = useSelector((state) => state.aplicationConfig.corporationData.discounts);

  const availableDiscounts = useMemo(
    () =>
      addIdKeyIfMissing(
        (Array.isArray(discounts) ? discounts : [])
          .filter((discount) => discount?.isActive && discount?.applicableTo === "order")
          .map((discount) => ({ ...discount })),
      ),
    [discounts],
  );
  const appliedDiscounts = Array.isArray(cart?.Discounts)
    ? cart.Discounts
    : Array.isArray(cart?.discounts)
      ? cart.discounts
      : [];

  const updateCartFromDiscountResponse = (data) => {
    const currentCart = store.getState().aplicationConfig.userConfig.cart;
    const nextDiscounts = Array.isArray(data?.discounts) ? data.discounts : appliedDiscounts;

    dispatch(
      updateCart({
        ...currentCart,
        ...data,
        Discounts: nextDiscounts,
        discounts: nextDiscounts,
      }),
    );
  };

  const applyDiscount = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError("");
    try {
      if (!(await ensureProductChangesSaved(dispatch))) {
        setError("No se pudieron guardar los productos. Reintenta antes de aplicar el descuento.");
        return;
      }
      const currentCart = store.getState().aplicationConfig.userConfig.cart;
      const discountId = document.getElementById("cart-discount")?.value ?? null;
      if (!currentCart?.shoppingCartId || !discountId) {
        setError("Selecciona un descuento y comprueba que el carrito sigue disponible.");
        return;
      }
      const response = await addCartDiscount(currentCart.shoppingCartId, discountId);
      if (response?.error === false && response?.data?.shoppingCartId === currentCart.shoppingCartId) {
        if (store.getState().aplicationConfig.userConfig.cart?.shoppingCartId === currentCart.shoppingCartId)
          updateCartFromDiscountResponse(response.data);
        dispatch(setAlert(translateMessage(response?.message || "Descuento aplicado")));
      } else {
        setError(response?.message || "No se confirmó el descuento. Revisa el carrito e inténtalo de nuevo.");
      }
    } catch (error) {
      console.error("[CartOptions] Error al aplicar el descuento:", error);
      setError("No se pudo aplicar el descuento. Comprueba la conexión e inténtalo de nuevo.");
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  const removeDiscount = async (discount) => {
    const discountId = getDiscountId(discount);
    if (!discountId) return;

    try {
      if (!(await ensureProductChangesSaved(dispatch))) return;

      const currentCart = store.getState().aplicationConfig.userConfig.cart;
      if (!currentCart?.shoppingCartId) return;

      const response = await removeCartDiscount(currentCart.shoppingCartId, discountId);
      if (response && !response.error) {
        setError("");
        updateCartFromDiscountResponse(response?.data);
        dispatch(setAlert(translateMessage(response?.message || "Descuento eliminado")));
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      }
    } catch (error) {
      console.error("[CartOptions] Error al eliminar el descuento:", error);
      dispatch(setAlert(translateMessage("Error al eliminar el descuento")));
    }
  };

  return (
    <div className="form" aria-busy={pending}>
      {appliedDiscounts.length > 0 && (
        <div className="applied-discounts">
          <b>Descuentos aplicados</b>
          {appliedDiscounts.map((discount, index) => {
            const discountId = getDiscountId(discount);
            return (
              <div className="applied-discount-item" key={discountId ?? index}>
                <span>{getDiscountName(discount)}</span>
                <Button text="Eliminar" icon={null} customClass="customButtonDanger" action={() => removeDiscount(discount)} />
              </div>
            );
          })}
        </div>
      )}

      <div>
        <Select id="cart-discount" label="" values={availableDiscounts} value={availableDiscounts?.[0]} callback={() => setError("")} />
      </div>

      {error && <p role="alert">{error}</p>}
      <div className="flex-end">
        <button type="button" className="discount-apply" disabled={pending} onClick={applyDiscount}>{pending ? "Aplicando…" : "Aplicar descuento"}</button>
        <button type="button" className="discount-close" disabled={pending} onClick={onClose}>Cerrar</button>
      </div>
    </div>
  );
};

const OptionModal = ({ children }) => <div className="optionModal">{children}</div>;

export const OrderOptions = () => {
  const dispatch = useDispatch();
  const order = useSelector((state) => state.aplicationConfig.userConfig.order);
  const [activeModal, setActiveModal] = useState(null);
  const [showOptions, setShowOptions] = useState(false);

  const canEdit = order?.status === "pending" && order?.paymentStatus !== "paid" &&
    (order.products || []).every(line => line.status === "pending");
  const closeModal = () => setActiveModal(null);
  const toggleOptions = () => setShowOptions((current) => !current);

  const openOptionModal = (modal) => {
    if (!canEdit) return;
    setActiveModal(modal);
    setShowOptions(false);
  };

  const printTicketQuery = async () => {
    try {
      if (!order?.orderId) return;
      if (!(await ensureProductChangesSaved(dispatch))) return;

      const response = await printTicket({ orderId: order.orderId });
      dispatch(setAlert(translateMessage(response?.message || "Bad request")));
      setShowOptions(false);
    } catch (error) {
      console.error("[OrderOptions] Error en printTicket:", error);
      dispatch(setAlert(translateMessage("Error de red al imprimir ticket")));
    }
  };

  return (
    <>
      {canEdit && activeModal === "discount" && (
        <OptionModal>
          <OrderDiscountComponent order={order} onClose={closeModal} />
        </OptionModal>
      )}
      {canEdit && activeModal === "notes" && (
        <OptionModal>
          <OrderNotesComponent order={order} onClose={closeModal} />
        </OptionModal>
      )}

      <div className="optionsMenuWrapper">
        {showOptions && (
          <div className="orderOptions optionsFloatingMenu">
            <button type="button" onClick={printTicketQuery} className="orderOption" aria-label="Imprimir ticket">
              <BsPrinter />
            </button>
            <button type="button" disabled={!canEdit} onClick={() => openOptionModal("discount")} className="orderOption" aria-label="Descuentos">
              <RiDiscountPercentLine />
            </button>
            <button type="button" disabled={!canEdit} onClick={() => openOptionModal("notes")} className="orderOption" aria-label="Notas">
              <MdEditNote />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={toggleOptions}
          className={`optionsMenuTrigger${showOptions ? " optionsMenuTriggerActive" : ""}`}
          aria-label={showOptions ? "Cerrar opciones" : "Mostrar opciones"}
          aria-expanded={showOptions}
        >
          {showOptions ? <MdClose /> : <MdMoreHoriz />}
        </button>
      </div>
    </>
  );
};

export const CartOptions = ({ clearCart }) => {
  const cart = useSelector((state) => state.aplicationConfig.userConfig.cart);
  const [activeModal, setActiveModal] = useState(null);
  const [showOptions, setShowOptions] = useState(false);

  const closeModal = () => setActiveModal(null);
  const toggleOptions = () => setShowOptions((current) => !current);

  const handleCartDiscount = () => {
    setActiveModal("discount");
    setShowOptions(false);
  };

  const handleClearCart = async () => {
    setShowOptions(false);
    if (typeof clearCart === "function") await clearCart();
  };

  return (
    <>
      {activeModal === "notes" && (
        <OptionModal>
          <CartNotesComponent cart={cart} onClose={closeModal} />
        </OptionModal>
      )}
      {activeModal === "discount" && (
        <OptionModal>
          <CartDiscountComponent cart={cart} onClose={closeModal} />
        </OptionModal>
      )}

      <div className="optionsMenuWrapper">
        {showOptions && (
          <div className="orderOptions cartOptions optionsFloatingMenu">
            <button type="button" onClick={handleCartDiscount} className="orderOption" aria-label="Descuentos del carrito">
              <RiDiscountPercentLine />
            </button>
            <button
              type="button"
              onClick={() => {
                setShowOptions(false);
                setActiveModal("notes");
              }}
              className="orderOption"
              aria-label="Notas del carrito"
            >
              <MdEditNote />
            </button>
            <button type="button" onClick={handleClearCart} className="orderOption orderOptionDanger" aria-label="Vaciar carrito">
              <BsTrash />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={toggleOptions}
          className={`optionsMenuTrigger${showOptions ? " optionsMenuTriggerActive" : ""}`}
          aria-label={showOptions ? "Cerrar opciones" : "Mostrar opciones"}
          aria-expanded={showOptions}
        >
          {showOptions ? <MdClose /> : <MdMoreHoriz />}
        </button>
      </div>
    </>
  );
};
